import { Component, OnInit, OnDestroy } from "@angular/core";
import { moveItemInArray } from "@angular/cdk/drag-drop";
import { NotificationToastService } from "src/app/services/notification-toast.service";
import { AuthService } from "src/app/services/auth.service";
import { ActivatedRoute } from "@angular/router";
import { StudyReportService } from "../../../services/study-report/study-report.service";
import { StudyReportSearchItem } from "src/app/model/study-report-search-item";

@Component({
  selector: "app-patient-crm-study-report",
  templateUrl: "./patient-crm-study-report.component.html",
  styleUrls: ["./patient-crm-study-report.component.css"],
})
export class PatientCrmStudyReportComponent implements OnInit, OnDestroy {
  canPublishForced: boolean;
  canPublish: boolean;
  canSendMail: boolean;
  loading: boolean;

  columnsDefinition: { field: string; title: string }[] = [
    { field: 'DateTime', title: 'Fecha del turno' },
    { field: 'DeliveryDate', title: 'Fecha entrega' },
    { field: 'Protocol', title: 'Protocolo' },
    { field: 'PracticeName', title: 'Práctica' },
    { field: 'ServiceName', title: 'Servicio' },
    { field: 'State', title: 'Estado' },
    { field: 'AdminControled', title: 'Ctrl. admin.' },
    { field: 'PublishedYesNo', title: 'Publicado' },
  ];

  _columnOrder: string[] = this.columnsDefinition.map(c => c.field);
  columnWidths: { [key: string]: number } = {};
  pendingColumnFilters: { [key: string]: string } = {};
  madeColumnFilters: { [key: string]: string } = {};
  _pendingSortColumn: string = null;
  _pendingSortDirection: 'asc' | 'desc' = 'asc';
  _madeSortColumn: string = null;
  _madeSortDirection: 'asc' | 'desc' = 'asc';
  _pendingAllItems: StudyReportSearchItem[] = [];
  _madeAllItems: StudyReportSearchItem[] = [];
  pendingDataSource: StudyReportSearchItem[] = [];
  madeDataSource: StudyReportSearchItem[] = [];

  private _resizingField: string = null;
  private _resizeStartX: number = 0;
  private _resizeStartWidth: number = 0;
  private _dragSourceField: string = null;
  private _resizeMouseMoveBound: (e: MouseEvent) => void;
  private _resizeMouseUpBound: (e: MouseEvent) => void;

  constructor(
    private notificationToastService: NotificationToastService,
    private authService: AuthService,
    private studyReportService: StudyReportService,
    private route: ActivatedRoute
  ) {
    this._resizeMouseMoveBound = (e: MouseEvent) => this.onResizeMouseMove(e);
    this._resizeMouseUpBound = (e: MouseEvent) => this.onResizeMouseUp(e);
  }

  get orderedColumnsDefinition(): { field: string; title: string }[] {
    return this._columnOrder
      .filter(f => this.columnsDefinition.some(c => c.field === f))
      .map(f => this.columnsDefinition.find(c => c.field === f));
  }

  getColumnWidth(field: string): number {
    return this.columnWidths[field] != null ? this.columnWidths[field] : 150;
  }

  private loadColumnState() {
    try {
      const savedOrder = sessionStorage.getItem('patient-crm-study-report.columns.v1');
      if (savedOrder) {
        const saved: string[] = JSON.parse(savedOrder);
        const valid = saved.filter(f => this.columnsDefinition.some(c => c.field === f));
        const missing = this.columnsDefinition.map(c => c.field).filter(f => !valid.includes(f));
        this._columnOrder = valid.concat(missing);
      }
      const savedWidths = sessionStorage.getItem('patient-crm-study-report.column-widths.v1');
      if (savedWidths) {
        this.columnWidths = JSON.parse(savedWidths);
      }
    } catch (e) { /* ignore */ }
  }

  private saveColumnState() {
    try {
      sessionStorage.setItem('patient-crm-study-report.columns.v1', JSON.stringify(this._columnOrder));
      sessionStorage.setItem('patient-crm-study-report.column-widths.v1', JSON.stringify(this.columnWidths));
    } catch (e) { /* ignore */ }
  }

  onHeaderDragStart(event: DragEvent, field: string) {
    this._dragSourceField = field;
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
    }
  }

  onHeaderDragOver(event: DragEvent) {
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }
  }

  onHeaderDrop(event: DragEvent, targetField: string) {
    event.preventDefault();
    if (this._dragSourceField && this._dragSourceField !== targetField) {
      const fromIndex = this._columnOrder.indexOf(this._dragSourceField);
      const toIndex = this._columnOrder.indexOf(targetField);
      if (fromIndex !== -1 && toIndex !== -1) {
        moveItemInArray(this._columnOrder, fromIndex, toIndex);
        this.saveColumnState();
      }
    }
  }

  onHeaderDragEnd() {
    this._dragSourceField = null;
  }

  startResize(event: MouseEvent, field: string) {
    event.preventDefault();
    event.stopPropagation();
    this._resizingField = field;
    this._resizeStartX = event.clientX;
    this._resizeStartWidth = this.getColumnWidth(field);
    document.addEventListener('mousemove', this._resizeMouseMoveBound);
    document.addEventListener('mouseup', this._resizeMouseUpBound);
  }

  private onResizeMouseMove(event: MouseEvent) {
    if (this._resizingField) {
      const diff = event.clientX - this._resizeStartX;
      const newWidth = Math.max(60, this._resizeStartWidth + diff);
      this.columnWidths[this._resizingField] = newWidth;
    }
  }

  private onResizeMouseUp(event: MouseEvent) {
    if (this._resizingField) {
      document.removeEventListener('mousemove', this._resizeMouseMoveBound);
      document.removeEventListener('mouseup', this._resizeMouseUpBound);
      this._resizingField = null;
      this.saveColumnState();
    }
  }

  ngOnDestroy() {
    document.removeEventListener('mousemove', this._resizeMouseMoveBound);
    document.removeEventListener('mouseup', this._resizeMouseUpBound);
  }

  sortByColumn(grid: 'pending' | 'made', field: string) {
    if (grid === 'pending') {
      if (this._pendingSortColumn === field) {
        this._pendingSortDirection = this._pendingSortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this._pendingSortColumn = field;
        this._pendingSortDirection = 'asc';
      }
    } else {
      if (this._madeSortColumn === field) {
        this._madeSortDirection = this._madeSortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this._madeSortColumn = field;
        this._madeSortDirection = 'asc';
      }
    }
    this.applyColumnFilters(grid);
  }

  getSortIcon(grid: 'pending' | 'made', field: string): string {
    const col = grid === 'pending' ? this._pendingSortColumn : this._madeSortColumn;
    const dir = grid === 'pending' ? this._pendingSortDirection : this._madeSortDirection;
    if (col !== field) { return 'unfold_more'; }
    return dir === 'asc' ? 'arrow_upward' : 'arrow_downward';
  }

  onColumnFilterInput(grid: 'pending' | 'made', field: string, value: string) {
    if (grid === 'pending') {
      this.pendingColumnFilters[field] = value;
    } else {
      this.madeColumnFilters[field] = value;
    }
    this.applyColumnFilters(grid);
  }

  applyColumnFilters(grid: 'pending' | 'made') {
    const allItems = grid === 'pending' ? this._pendingAllItems : this._madeAllItems;
    const filters = grid === 'pending' ? this.pendingColumnFilters : this.madeColumnFilters;
    const sortColumn = grid === 'pending' ? this._pendingSortColumn : this._madeSortColumn;
    const sortDirection = grid === 'pending' ? this._pendingSortDirection : this._madeSortDirection;
    let filtered = allItems != null ? allItems.slice() : [];
    Object.keys(filters).forEach(field => {
      const filterVal = filters[field];
      if (filterVal && filterVal.trim() !== '') {
        const lower = filterVal.toLowerCase();
        filtered = filtered.filter(item => {
          const cell = this.formatCellValue(item, field);
          return cell.toLowerCase().includes(lower);
        });
      }
    });
    if (sortColumn) {
      filtered.sort((a, b) => {
        const av = this.formatCellValue(a, sortColumn);
        const bv = this.formatCellValue(b, sortColumn);
        const cmp = av.localeCompare(bv);
        return sortDirection === 'asc' ? cmp : -cmp;
      });
    }
    if (grid === 'pending') {
      this.pendingDataSource = filtered;
    } else {
      this.madeDataSource = filtered;
    }
  }

  private pad2(n: number): string {
    return n < 10 ? '0' + n : String(n);
  }

  private formatDate(date: any): string {
    if (date == null) { return ''; }
    const d = new Date(date);
    if (isNaN(d.getTime())) { return ''; }
    return this.pad2(d.getDate()) + '/' + this.pad2(d.getMonth() + 1) + '/' + d.getFullYear()
      + ' ' + this.pad2(d.getHours()) + ':' + this.pad2(d.getMinutes());
  }

  formatCellValue(element: StudyReportSearchItem, field: string): string {
    if (element == null) { return ''; }
    if (field === 'DateTime') { return this.formatDate(element.DateTime); }
    if (field === 'DeliveryDate') { return this.formatDate(element.DeliveryDate); }
    const val = element[field];
    return val != null ? String(val) : '';
  }

  ngOnInit() {
    this.loadColumnState();
    this.loading = true;
    this.canPublishForced = this.authService.canPublishForced();
    this.canPublish = this.authService.canPublish();
    this.canSendMail = this.authService.canSendStudyMail();
    this.route.params.subscribe((params) => {
      this.studyReportService
        .getStudyReportSearchItems(
          "",
          false,
          true,
          true,
          true,
          false,
          false,
          false,
          null,
          null,
          null,
          null,
          null,
          null,
          params["Id"],
          null,
          null
        )
        .then((info) => {
          if (info) {
            this._pendingAllItems = info.filter((i) => i.IsPendiet === true);
            this._madeAllItems = info.filter((i) => i.IsPendiet === false);
            this.applyColumnFilters('pending');
            this.applyColumnFilters('made');
          }
          this.loading = false;
        })
        .catch((err) =>
          this.notificationToastService.error(
            JSON.stringify(err.error),
            "Informes"
          )
        );
    });
  }

  async sendMail(item: StudyReportSearchItem) {
    try {
      item.Loading =  true;
      await this.studyReportService.sendMail(item);
    } catch (err) {
      this.notificationToastService.error(JSON.stringify(err.error), "Informe");
    } finally {
      item.Loading = false;
    }
  }

  async publishWeb(item: StudyReportSearchItem) {
    try {
      item.Loading =  true;
      await this.studyReportService.publishWeb([item]);
    } catch (err) {
      this.notificationToastService.error(JSON.stringify(err.error), "Informe");
    } finally {
      item.Loading =  false;
    }
  }

  async noPublishWeb(item: StudyReportSearchItem) {
    try {
      item.Loading = true;
      await this.studyReportService.noPublishWeb([item]);
    } catch (err) {
      this.notificationToastService.error(JSON.stringify(err.error), "Informe");
    } finally {
      item.Loading = false;
    }
  }

  async publishWebForced(item: StudyReportSearchItem) {
    try {
      item.Loading = true;
      await this.studyReportService.publishWebForced([item]);
      item.PublishWebForced = true;
    } catch (err) {
      this.notificationToastService.error(JSON.stringify(err.error), "Informe");
    } finally {
      item.Loading = false;
    }
  }

  async noPublishWebForced(item: StudyReportSearchItem) {
    try {
      item.Loading = true;
      await this.studyReportService.noPublishWebForced([item]);
      item.PublishWebForced = false;
    } catch (err) {
      this.notificationToastService.error(JSON.stringify(err.error), "Informe");
    } finally {
      item.Loading = false;
    }
  }
}
